import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, InputText, Select, TextArea, SectionCard } from "@/deps/ui-library";
import { HttpError } from "@/api/http";
import { useRaiseSupportTicket, useSupportCategories } from "@/api/queries";
import type { NewSupportTicket } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FilePicker } from "./FilePicker";
import { HelpGate } from "./HelpGate";
import { PRIORITIES, textToHtml } from "./supportLabels";

/** The value an RDS Select hands back, as the plain string the form holds. */
function picked(value: { value: string | number } | Array<{ value: string | number }> | null) {
  const option = Array.isArray(value) ? value[0] : value;
  return option ? String(option.value) : "";
}

/**
 * Raise a ticket. The only checks here stop an empty form being sent; what the platform refuses it
 * says, and that sentence is shown as it gave it.
 */
export function NewSupportTicketPage() {
  const navigate = useNavigate();
  const categories = useSupportCategories();
  const raise = useRaiseSupportTicket();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState<NewSupportTicket["customerPriority"]>("MEDIUM");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const ready = title.trim() !== "" && category !== "" && description.trim() !== "";
  const refusal =
    raise.error instanceof HttpError
      ? raise.error.message
      : raise.error
        ? "The ticket could not be raised."
        : null;

  return (
    <HelpGate section="tickets">
      <Breadcrumbs
        items={[{ label: "My tickets", to: "/help/tickets" }, { label: "New ticket" }]}
      />
      <SectionCard title="Raise a ticket">
        <form
          className="flex max-w-2xl flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready) return;
            raise.mutate(
              {
                title: title.trim(),
                category,
                customerPriority: priority,
                descriptionHtml: textToHtml(description),
                files,
              },
              {
                onSuccess: (detail) =>
                  void navigate({ to: "/help/tickets/$id", params: { id: detail.ticket.id } }),
              },
            );
          }}
        >
          {refusal ? (
            <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refusal} />
          ) : null}
          <InputText
            label="What is it about?"
            required
            value={title}
            placeholder="For example: the VAT return does not match the register"
            onChange={(event) => setTitle(event.target.value)}
          />
          <Select
            label="Area"
            required
            placeholder="Choose an area"
            options={(categories.data ?? []).map((name) => ({ value: name, label: name }))}
            value={category}
            onChange={(details) => setCategory(picked(details.value))}
            helperText={
              categories.data?.length === 0
                ? "No areas are set up yet. Rigo sets these up for your product."
                : undefined
            }
            showClearButton={false}
          />
          <Select
            label="How urgent is it?"
            options={PRIORITIES.map((option) => ({ value: option.value, label: option.label }))}
            value={priority}
            onChange={(details) =>
              setPriority(
                (picked(details.value) || "MEDIUM") as NewSupportTicket["customerPriority"],
              )
            }
            showClearButton={false}
          />
          <TextArea
            label="What happened?"
            required
            rows={6}
            value={description}
            helperText="What you did, what you expected, and what happened instead."
            onChange={(event) => setDescription(event.target.value)}
          />
          <FilePicker files={files} onChange={setFiles} />
          <div className="flex justify-end gap-2">
            <Button
              colorScheme="default"
              variant="outline"
              onClick={() => void navigate({ to: "/help/tickets" })}
            >
              Cancel
            </Button>
            <Button colorScheme="primary" type="submit" disabled={!ready} loading={raise.isPending}>
              Raise ticket
            </Button>
          </div>
        </form>
      </SectionCard>
    </HelpGate>
  );
}
